/**
 * Payload slimming helpers: strip unused CMS image variants and truncate
 * blog/news lists so `__NUXT_DATA__` stays small.
 */

const IMAGE_KEEP = new Set([
  "url",
  "urlMobile",
  "thumbUrl",
  "alt",
  "width",
  "height",
  "thumbWidth",
  "thumbHeight",
  "id",
  "uuid",
  "title1",
  "caption",
]);

const SRCSET_KEYS = new Set([
  "srcset",
  "srcsetWebp",
  "srcsetAvif",
  "sizes",
]);

const ARTICLE_LIST_LIMIT = 12;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function looksLikeImageObject(value: Record<string, unknown>): boolean {
  // Require a file-like shape: url plus at least one image-dimension or srcset field,
  // and no rich content keys that belong to Kirby blocks/layouts.
  if (typeof value.url !== "string") return false;
  if (
    "content" in value ||
    "blocks" in value ||
    "columns" in value ||
    "layouts" in value ||
    "type" in value ||
    "id" in value && "attrs" in value
  ) {
    return false;
  }
  return (
    "srcset" in value ||
    "srcsetWebp" in value ||
    "srcsetAvif" in value ||
    "width" in value ||
    "height" in value ||
    ("alt" in value && ("urlMobile" in value || "thumbUrl" in value || "sizes" in value))
  );
}

export function slimImage<T>(image: T): T {
  if (!isPlainObject(image)) return image;
  if (!looksLikeImageObject(image)) return image;

  const slim: Record<string, unknown> = {};
  for (const key of IMAGE_KEEP) {
    if (key in image && image[key] != null) slim[key] = image[key];
  }
  return slim as T;
}

/** Recursively remove srcset triples and slim image-like objects. */
export function stripHeavyImageFields<T>(value: T, depth = 0): T {
  if (depth > 30 || value == null) return value;

  if (Array.isArray(value)) {
    return value.map((item) => stripHeavyImageFields(item, depth + 1)) as T;
  }

  if (!isPlainObject(value)) return value;

  if (looksLikeImageObject(value)) {
    return slimImage(value);
  }

  const out: Record<string, unknown> = {};
  for (const [key, child] of Object.entries(value)) {
    if (SRCSET_KEYS.has(key)) continue;
    out[key] = stripHeavyImageFields(child, depth + 1);
  }
  return out as T;
}

/** Parse CMS `d/MM/Y` (or `dd/MM/yyyy`) published strings for sorting. */
function articlePublishedTime(published: unknown): number {
  if (typeof published !== "string" || !published) return 0;
  const parts = published.split("/").map(Number);
  const [day, month, year] = parts;
  if (day && month && year) {
    return new Date(year, month - 1, day).getTime();
  }
  return 0;
}

/**
 * Slim article cards and optionally cap the list.
 * Always sort by published date (newest first) before truncating — Kirby
 * children order is not publish order, so slicing first dropped recent posts.
 * Pass `limit: null` to keep the full sorted list (blog/news index pages).
 */
export function slimArticleList<T>(
  articles: T[] | null | undefined,
  limit: number | null = ARTICLE_LIST_LIMIT,
): T[] {
  if (!Array.isArray(articles)) return [];

  const sorted = articles.slice().sort((a, b) => {
    const aPublished = isPlainObject(a) ? a.published : null;
    const bPublished = isPlainObject(b) ? b.published : null;
    return articlePublishedTime(bPublished) - articlePublishedTime(aPublished);
  });

  const capped =
    limit == null || !Number.isFinite(limit)
      ? sorted
      : sorted.slice(0, limit);

  return capped.map((article) => {
    if (!isPlainObject(article)) return article;
    const {
      uri,
      title,
      published,
      tags,
      blogImage,
      blogImage1,
      slug,
      id,
    } = article;
    return stripHeavyImageFields({
      uri,
      id,
      slug,
      title,
      published,
      tags,
      blogImage: slimImage(blogImage),
      blogImage1: slimImage(blogImage1),
    }) as T;
  });
}

/**
 * Site-wide nav only: title, menuText, uri/slug, listing flags, and shallow
 * children. No images, trek stats, or deep subSubPages trees.
 */
function slimNavChild(
  child: Record<string, unknown>,
  depth = 0,
): Record<string, unknown> {
  const base: Record<string, unknown> = {
    id: child.id,
    title: child.title,
    uri: child.uri,
    slug: child.slug,
    menuText: child.menuText,
    isListed: child.isListed,
    template: child.template,
    trekLocation: child.trekLocation,
  };

  // One level of subPages only on trek hubs (FAQ path resolution)
  if (
    depth === 0 &&
    child.template === "treks" &&
    Array.isArray(child.subPages)
  ) {
    base.subPages = child.subPages.map((c) =>
      isPlainObject(c)
        ? {
            title: c.title,
            uri: c.uri,
            slug: c.slug,
            menuText: c.menuText,
            isListed: c.isListed,
            template: c.template,
          }
        : c,
    );
  }

  if (depth === 0 && Array.isArray(child.children)) {
    base.children = child.children.map((c) =>
      isPlainObject(c) ? slimNavChild(c, depth + 1) : c,
    );
  }

  return base;
}

/** Slim global site blob used in every page payload. */
export function slimSitePayload<T extends Record<string, unknown>>(site: T): T {
  if (!site) return site;

  const children = Array.isArray(site.children)
    ? site.children.map((child) =>
        isPlainObject(child) ? slimNavChild(child) : child,
      )
    : site.children;

  const keep = stripHeavyImageFields({
    title: site.title,
    description: site.description,
    emailContact: site.emailContact,
    phone: site.phone,
    address: site.address,
    announcementLive: site.announcementLive,
    announcementText: site.announcementText,
    announcementLink: site.announcementLink,
    announcementText2: site.announcementText2,
    announcementLink2: site.announcementLink2,
    comingSoonTrek: site.comingSoonTrek,
    icons: site.icons,
    comps: site.comps,
    logo: slimImage(site.logo),
    // Header + homepage / classic / worldwide award badges
    feefo: slimImage(site.feefo),
    feefoMobile: slimImage(site.feefoMobile),
    feefoBlue: slimImage(site.feefoBlue),
    feefoMobileBlue: slimImage(site.feefoMobileBlue),
    children,
  });

  return keep as T;
}

/**
 * Slim page data before storing in useState / after CMS fetch:
 * strip srcsets, cap blog/news article lists. Never rewrite `layouts`
 * trees (Kirby blocks can look like images and get destroyed).
 */
export function slimPagePayload<T>(page: T): T {
  if (!isPlainObject(page)) return page;

  const layouts = page.layouts;
  const next: Record<string, unknown> = { ...page };

  if (Array.isArray(next.blogArticles)) {
    // Featured embeds on trek/other pages: newest N only
    next.blogArticles = slimArticleList(next.blogArticles as unknown[]);
  }
  if (Array.isArray(next.newsArticles)) {
    next.newsArticles = slimArticleList(next.newsArticles as unknown[]);
  }
  if (Array.isArray(next.articles)) {
    // Blog/news index + homepage lists: keep full chronology (already field-slimmed)
    next.articles = slimArticleList(next.articles as unknown[], null);
  }

  // Galleries are huge: keep cover only
  if (Array.isArray(next.galleries)) {
    next.galleries = (next.galleries as unknown[]).map((g) => {
      if (!isPlainObject(g)) return g;
      return stripHeavyImageFields({
        uri: g.uri,
        id: g.id,
        slug: g.slug,
        title: g.title,
        coverImage: g.coverImage,
      });
    });
  }

  // CMS trek_link fields often include trailing newlines; those become %0A and 404.
  if (Array.isArray(next.trekPackagesList)) {
    next.trekPackagesList = (next.trekPackagesList as unknown[]).map((pkg) => {
      if (!isPlainObject(pkg)) return pkg;
      const link = pkg.trek_link;
      if (typeof link !== "string") return pkg;
      const trimmed = link.trim();
      return trimmed === link ? pkg : { ...pkg, trek_link: trimmed };
    });
  }

  // Slim known top-level image fields without walking layouts
  for (const key of [
    "heroImage",
    "heroVideoPoster",
    "trekVideoPoster",
    "featureImage",
    "ogImage",
    "coverImage",
  ]) {
    if (key in next) next[key] = slimImage(next[key]);
  }

  // Page file library: Kirby image/gallery/accordion/video blocks resolve
  // assets by uuid via usePage().images — keep lookup fields only.
  if (Array.isArray(next.images)) {
    next.images = (next.images as unknown[]).map((img) => {
      if (!isPlainObject(img)) return img;
      const slim: Record<string, unknown> = {};
      for (const key of IMAGE_KEEP) {
        if (key in img && img[key] != null) slim[key] = img[key];
      }
      return slim;
    });
  }

  // Strip srcset keys everywhere except layouts (preserve block HTML for SSR)
  delete next.layouts;
  const stripped = stripSrcsetKeysOnly(next);
  if (layouts !== undefined) {
    (stripped as Record<string, unknown>).layouts = layouts;
  }
  return stripped as T;
}

/** Remove srcset/sizes keys recursively without reshaping objects. */
function stripSrcsetKeysOnly<T>(value: T, depth = 0): T {
  if (depth > 40 || value == null) return value;
  if (Array.isArray(value)) {
    return value.map((item) => stripSrcsetKeysOnly(item, depth + 1)) as T;
  }
  if (!isPlainObject(value)) return value;
  const out: Record<string, unknown> = {};
  for (const [key, child] of Object.entries(value)) {
    if (SRCSET_KEYS.has(key)) continue;
    out[key] = stripSrcsetKeysOnly(child, depth + 1);
  }
  return out as T;
}

/**
 * Fields safe to omit from global useState('app.page').
 * Do NOT omit `images`: Kirby Block Image/Gallery/Accordion/Video resolve
 * files by uuid from usePage().images (layouts stay in the page fetch).
 */
const PAGE_STATE_OMIT = new Set([
  "layouts",
  "galleries",
  "slideshow",
  // trekPageTrek / faqPage / trekPage stay in page state: TrekItinerary,
  // TrekFaqs, and TrekMap blocks read them via useTrekLandingData → usePage().
  // (Already filtered to the current trek by slimTrekLandingData2.)
  // trekPackagesList / trekprices* stay for the same reason (AppTrekPackages).
  // essentialInfo stays: AppInfoNav reads it from usePage() on /essential-info.
]);

/**
 * Minimal shell for useState('app.page') used by heroes/heading/header.
 * Avoids duplicating layouts/galleries already stored in useAsyncData.
 */
export function slimPageStatePayload<T>(page: T): T {
  const slim = slimPagePayload(page);
  if (!isPlainObject(slim)) return slim;
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(slim)) {
    if (PAGE_STATE_OMIT.has(key)) continue;
    out[key] = value;
  }
  return out as T;
}

function normalizeTrekMatch(value: unknown): string {
  return String(value ?? "")
    .toLowerCase()
    .trim()
    .replace(/['"]/g, "");
}

/** True when a package/price/map row belongs to this trek hub. */
export function matchesTrekRoute(
  routeOrSelect: unknown,
  page: Record<string, unknown>,
): boolean {
  const route = normalizeTrekMatch(routeOrSelect);
  if (!route) return false;

  const candidates = [
    page.parentPageTitle,
    page.title,
    page.menuText,
    // First path segment is usually the hub slug ("the-italian-dolomites")
    String(page.uri ?? "")
      .split("/")
      .filter(Boolean)[0]
      ?.replace(/-/g, " "),
  ]
    .map(normalizeTrekMatch)
    .filter(Boolean);

  for (const candidate of candidates) {
    if (
      candidate === route ||
      candidate.includes(route) ||
      route.includes(candidate)
    ) {
      return true;
    }
  }
  return false;
}

/**
 * Filter trekprices / packages lists to the current trek (same match as
 * AppTrekPackages). Used for JSON-LD offers and payload trimming.
 */
export function filterTrekPriceRows<T extends Record<string, unknown>>(
  rows: T[] | null | undefined,
  page: Record<string, unknown>,
): T[] {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  return rows.filter((row) => matchesTrekRoute(row?.trek_route, page));
}

/**
 * Shrink template=trek itinerary pages: drop unused blobs (mapData is ~140KB
 * and MapboxMain lazy-fetches /api/map-data), keep only this trek's packages
 * and prices, and slim trekPage to nav fields.
 */
export function slimTrekItineraryPage<T extends Record<string, unknown>>(
  page: T,
): T {
  if (!isPlainObject(page)) return page;
  if (page.template !== "trek") return page;

  const next: Record<string, unknown> = { ...page };

  // ClientOnly MapboxMain loads markers via /api/map-data when missing
  delete next.mapData;
  // Unused on trek itinerary template
  delete next.questions;
  delete next.answers;
  delete next.images;
  delete next.featureVideos;
  delete next.faqPage;
  delete next.trekPageTrek;
  delete next.galleries;
  delete next.slideshow;
  delete next.essentialInfo;

  if (Array.isArray(next.trekPage)) {
    next.trekPage = (next.trekPage as Record<string, unknown>[]).map(
      (item) => ({
        uri: item.uri,
        id: item.id,
        slug: item.slug,
        title: item.title,
        menuText: item.menuText,
        template: item.template,
        isListed: item.isListed,
      }),
    );
  }

  if (Array.isArray(next.trekPackagesList)) {
    next.trekPackagesList = filterTrekPriceRows(
      next.trekPackagesList as Record<string, unknown>[],
      next,
    );
  }
  if (Array.isArray(next.trekprices1)) {
    next.trekprices1 = filterTrekPriceRows(
      next.trekprices1 as Record<string, unknown>[],
      next,
    );
  }
  if (Array.isArray(next.trekprices2)) {
    next.trekprices2 = filterTrekPriceRows(
      next.trekprices2 as Record<string, unknown>[],
      next,
    );
  }

  // Itinerary images: keep display fields only
  if (Array.isArray(next.trekItinerary)) {
    next.trekItinerary = (next.trekItinerary as Record<string, unknown>[]).map(
      (day) => {
        const img = day?.it_image as Record<string, unknown> | undefined;
        return {
          title: day.title,
          text: day.text,
          daily_stats: day.daily_stats,
          guide_notes: day.guide_notes,
          accommodation: day.accommodation,
          it_image:
            img && typeof img.url === "string"
              ? {
                  url: img.url,
                  alt: img.alt ?? "",
                  width: img.width,
                  height: img.height,
                }
              : img ?? null,
        };
      },
    );
  }

  return next as T;
}
